import { useTranslation } from "react-i18next";
import { PageBody } from "@/components/ui/PageHeader";
import { Panel, PanelHeader } from "@/components/ui/Panel";
import { KpiTile } from "@/components/domain/KpiTile";
import { BarMeter } from "@/components/charts/BarMeter";
import { LoadingState, QueryErrorState } from "@/components/ui/States";
import { useAnalytics } from "@/api/hooks";
import { PERIOD_PRESETS, PeriodFilter, dayToMs, presetRange, type PeriodPreset } from "@/components/domain/PeriodFilter";
import { useListQueryState } from "@/lib/listQuery";
import { CurrencyNotice } from "@/components/domain/CurrencyNotice";
import { formatCompactMoney, formatMoney } from "@/domain/money";
import { ExportButton } from "@/components/domain/ExportButton";
import { analyticsColumns, buildAnalyticsRows, type AnalyticsCsvRow } from "@/lib/analyticsCsv";
import { useDataRegion } from "@/app/access";

/** Xem riêng từng đội (Roary 29/09/2026) — chỉ cho tài khoản thấy cả hai khu vực; tài khoản một khu vực vốn chỉ có một đội. */
const TEAMS = ["all", "vietnam", "china"] as const;
type Team = (typeof TEAMS)[number];

export function AnalyticsPage() {
  const { t } = useTranslation();
  const list = useListQueryState();
  // Kỳ (yêu cầu v1.8 mục 1.2): mở lần đầu = "Hôm qua" cho cả hai công ty; bấm kỳ nhanh thì ghi `period`, gõ ngày tay thì ghi
  // `from`/`to` và bỏ kỳ nhanh. Ngày của kỳ nhanh điền vào hai ô để người xem thấy chính xác kỳ đang tính.
  const rawFrom = list.searchParams.get("from") ?? "";
  const rawTo = list.searchParams.get("to") ?? "";
  const periodParam = list.searchParams.get("period");
  const preset: PeriodPreset | null = PERIOD_PRESETS.includes(periodParam as PeriodPreset)
    ? (periodParam as PeriodPreset)
    : !rawFrom && !rawTo
      ? "day"
      : null;
  const presetDates = preset ? presetRange(preset) : null;
  const fromStr = presetDates?.from ?? rawFrom;
  const toStr = presetDates?.to ?? rawTo;
  const ownRegion = useDataRegion();
  const teamParam = list.searchParams.get("team");
  const team: Team = !ownRegion && TEAMS.includes(teamParam as Team) ? (teamParam as Team) : "all";
  const range = { from: dayToMs(fromStr, false), to: dayToMs(toStr, true), region: team === "all" ? undefined : team };
  const resetPeriod = () => list.updateParams({ from: null, to: null, period: null });
  const choosePreset = (p: PeriodPreset) => list.updateParams({ from: null, to: null, period: p === "day" ? null : p });
  const changeDate = (key: "from" | "to", value: string | null) => {
    // Sửa một ô khi đang ở kỳ nhanh: giữ ngày còn lại của kỳ đó, chuyển sang kỳ tự chọn.
    list.updateParams(
      preset
        ? { from: fromStr, to: toStr, period: "custom", [key]: value }
        : { [key]: value },
    );
  };
  const teamLabel = (x: Team) => (x === "all" ? t("common.all") : t(x === "china" ? "customers.china" : "customers.vietnam"));
  const { data: a, isLoading, error, refetch } = useAnalytics(range);
  if (isLoading) return <PageBody><LoadingState /></PageBody>;
  if (error || !a) return <PageBody><QueryErrorState error={error} message={t("analytics.errorLoad")} forbiddenMessage={t("errors.forbidden")} onRetry={() => refetch()} /></PageBody>;
  // 24 kỳ gần nhất. Không giới hạn thì mỗi cột hẹp dần tới khi không đọc nổi,
  // và nó chỉ tệ thêm theo thời gian chứ không bao giờ tự tốt lên.
  const months = a.byMonth.slice(-24);
  const maxMonth = Math.max(...months.map((m) => m.revenue), 1);
  // Kỳ / đội chưa có PI (ví dụ đội Trung Quốc khi chưa lập PI nào) thì mỗi khối ghi rõ là không có dữ liệu — khung trắng trống
  // trông như trang lỗi hoặc chưa tải xong (soát production 30/09/2026).
  const noData = <p className="py-6 text-center text-sm text-ink-faint">{t("analytics.noData")}</p>;
  const bars = (rows: Parameters<typeof BarMeter>[0]["rows"]) => (rows.length ? <BarMeter rows={rows} /> : noData);
  return (
    <PageBody>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-wrap items-start gap-3">
          <PeriodFilter from={fromStr} to={toStr} onChange={changeDate} onReset={resetPeriod} period={a.period} preset={preset} onPreset={choosePreset} />
          {ownRegion ? null : (
            <div role="group" aria-label={t("analytics.region")} title={t("analytics.regionHint")} className="flex items-center gap-2">
              <span className="text-xs font-medium text-ink-soft">{t("analytics.region")}</span>
              <div className="flex items-center rounded-sm border border-line bg-surface p-0.5">
                {TEAMS.map((x) => (
                  <button
                    key={x}
                    type="button"
                    aria-pressed={team === x}
                    onClick={() => list.updateParam("team", x === "all" ? null : x)}
                    className={
                      "rounded-[3px] px-2.5 py-1 text-xs font-medium transition-colors " +
                      (team === x ? "bg-brand text-brand-on" : "text-ink-faint hover:text-ink-soft")
                    }
                  >
                    {teamLabel(x)}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <ExportButton<AnalyticsCsvRow>
          name="analytics"
          title={team === "all" ? t("nav.analytics") : `${t("nav.analytics")} — ${teamLabel(team)}`}
          rows={buildAnalyticsRows(a, t)}
          columns={analyticsColumns(t)}
          errorKey="analytics.errorLoad"
          rowKey={(r) => `${r.dimension}/${r.label}`}
        />
      </div>
      <CurrencyNotice excluded={a.excludedCurrencies} />
      {/* ĐẾM THẬT từ máy chủ. Trước đây dùng byProduct.length / byCustomer.length,
          mà hai mảng đó đã bị cắt còn top-8 — nên hai KPI luôn tối đa bằng 8. */}
      <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
        <KpiTile label={t("analytics.totalBilled")} value={formatCompactMoney(a.totalRevenue, a.currency)} />
        <KpiTile label={t("analytics.orders")} value={String(a.orderCount)} />
        <KpiTile label={t("analytics.avgOrderValue")} value={formatCompactMoney(a.averageOrderValue, a.currency)} />
        <KpiTile label={t("analytics.totalQuantity")} value={a.totalQuantity.toLocaleString()} />
        <KpiTile label={t("analytics.productsSold")} value={String(a.productCount)} />
        <KpiTile label={t("analytics.activeCustomers")} value={String(a.customerCount)} />
      </div>
      <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
        <Panel><PanelHeader title={t("analytics.topProductsRevenue")} /><div className="p-4">
          {bars(a.byProduct.map((p) => ({ label: p.code, value: p.revenue, display: formatCompactMoney(p.revenue, a.currency), tone: "var(--color-brand)" })))}</div></Panel>
        <Panel><PanelHeader title={t("analytics.topCustomersRevenue")} /><div className="p-4">
          {bars(a.byCustomer.map((cu) => ({ label: cu.name, value: cu.revenue, display: formatCompactMoney(cu.revenue, a.currency), tone: "var(--color-brass)" })))}</div></Panel>
      </div>
      <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
        <Panel><PanelHeader title={t("analytics.byRespBy")} /><div className="p-4">
          {bars(a.byRespBy.map((r) => ({ label: `${r.name} (${r.orders})`, value: r.revenue, display: formatCompactMoney(r.revenue, a.currency), tone: "var(--color-ok)" })))}</div></Panel>
        <Panel><PanelHeader title={t("analytics.byProductType")} /><div className="p-4">
          {bars(a.byProductType.map((r) => ({ label: r.type, value: r.revenue, display: formatCompactMoney(r.revenue, a.currency), tone: "var(--color-info)" })))}</div></Panel>
      </div>
      <Panel className="mt-3"><PanelHeader title={t("analytics.revenueByMonth")} /><div className="p-4">
        {months.some((m) => m.revenue) ? <div className="flex items-end gap-2" style={{ height: 160 }}>
          {months.map((m) => (
            <div key={m.month} className="flex flex-1 flex-col items-center justify-end gap-1">
              <span className="font-mono text-2xs text-ink-soft">{formatCompactMoney(m.revenue, a.currency)}</span>
              <div className="w-full rounded-t-sm bg-brand" style={{ height: `${(m.revenue / maxMonth) * 120}px` }} title={formatMoney(m.revenue, a.currency)} />
              <span className="font-mono text-2xs text-ink-faint">{m.month}</span>
            </div>
          ))}
        </div> : noData}</div></Panel>
    </PageBody>
  );
}
